
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from "date-fns";
import { cn } from "@/lib/utils";
import { WorkDay } from "../../types/schedule";

interface ScheduleCalendarProps {
  workDays: WorkDay[];
  onDayClick: (date: Date, workDay?: WorkDay) => void;
}

const ScheduleCalendar = ({ workDays, onDayClick }: ScheduleCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const previousMonth = () => {
    setCurrentMonth((prev) => subMonths(prev, 1));
  };

  const nextMonth = () => {
    setCurrentMonth((prev) => addMonths(prev, 1));
  };

  const getWorkDayForDate = (date: Date) => {
    return workDays.find((workDay) => isSameDay(new Date(workDay.date), date));
  };

  const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <Card className="w-full">
      <CardHeader className="px-6 pb-0">
        <div className="flex items-center justify-between">
          <CardTitle>{format(currentMonth, "MMMM yyyy")}</CardTitle>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="icon" onClick={previousMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={nextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-7 gap-2 text-center text-sm mb-2">
          {weekdayNames.map((day) => (
            <div key={day} className="font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay() }).map((_, i) => (
            <div key={`empty-${i}`} className="h-12 sm:h-24" />
          ))}
          
          {daysInMonth.map((day) => {
            const workDay = getWorkDayForDate(day);
            const isToday = isSameDay(day, new Date());
            
            return (
              <div
                key={day.toString()}
                className={cn(
                  "h-12 sm:h-24 border rounded-md p-1 overflow-hidden cursor-pointer transition-colors",
                  isToday ? "border-primary" : "border-border",
                  workDay ? "hover:border-primary" : "hover:bg-muted/50",
                )}
                onClick={() => onDayClick(day, workDay)}
              >
                <div className="flex flex-col h-full">
                  <div className={cn(
                    "text-right text-xs font-medium mb-1",
                    isToday ? "text-primary" : "text-foreground"
                  )}>
                    {format(day, "d")}
                  </div>
                  {workDay && (
                    <div className="flex-1 text-xs">
                      <Badge className="bg-primary/20 text-primary border-primary/20 px-1 py-0 text-[10px] truncate" variant="outline">
                        {workDay.shifts.length} {workDay.shifts.length === 1 ? "shift" : "shifts"}
                      </Badge>
                      <div className="hidden sm:flex flex-col gap-1 mt-1 overflow-hidden">
                        {workDay.shifts.slice(0, 2).map((shift, i) => (
                          <div key={i} className="bg-muted text-[10px] px-1 py-0.5 rounded truncate">
                            {shift.role}
                          </div>
                        ))}
                        {workDay.shifts.length > 2 && (
                          <div className="text-[10px] text-muted-foreground">
                            +{workDay.shifts.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ScheduleCalendar;
